import type { DeterrenceDevice } from "../g2-fleet-messages/commands";

/** The devices a user can test, in the order the controls list them. */
export const DETERRENCE_TEST_DEVICES: readonly DeterrenceDevice[] = ["siren", "strobe"];

/**
 * How long a device's test button stays disabled after a command is sent.
 *
 * Each press really fires the hardware, and the edge holds it on for about 5s
 * (20s on VA assets). Long enough to stop a double-tap firing it twice, short
 * enough not to get in the way of a deliberate retry.
 */
export const DETERRENCE_TEST_HOLD_MS = 10_000;

export function isDeterrenceTestHeld(lastSentAt: number | undefined, now: number): boolean {
  if (lastSentAt === undefined) return false;
  return now - lastSentAt < DETERRENCE_TEST_HOLD_MS;
}

/**
 * The deterrence-test hold state for one camera (`subject`): the device whose
 * command is in flight, and when each device was last sent a command.
 */
export interface DeterrenceTestState {
  readonly subject: string;
  readonly sending: DeterrenceDevice | null;
  readonly sentAt: Readonly<Partial<Record<DeterrenceDevice, number>>>;
}

export function emptyDeterrenceTestState(subject: string): DeterrenceTestState {
  return { subject, sending: null, sentAt: {} };
}

/** The state for `subject`. A camera switch starts clean; nothing carries over. */
export function deterrenceTestStateFor(state: DeterrenceTestState, subject: string): DeterrenceTestState {
  return state.subject === subject ? state : emptyDeterrenceTestState(subject);
}

/** Whether `device` on `subject` is unavailable: a command in flight, or one sent within the hold. */
export function isDeterrenceTestBusy(
  state: DeterrenceTestState,
  subject: string,
  device: DeterrenceDevice,
  now: number,
): boolean {
  if (state.subject !== subject) return false;
  return state.sending !== null || isDeterrenceTestHeld(state.sentAt[device], now);
}

/** Marks `device` on `subject` as sending, or returns `null` when it is busy and the send must be refused. */
export function startDeterrenceTest(
  state: DeterrenceTestState,
  subject: string,
  device: DeterrenceDevice,
  now: number,
): DeterrenceTestState | null {
  const scoped = deterrenceTestStateFor(state, subject);
  if (isDeterrenceTestBusy(scoped, subject, device, now)) return null;
  return { ...scoped, sending: device };
}

/**
 * Settles a send made for `sentFor`. A response for another camera never
 * touches this state; a successful one holds the device from `now`.
 */
export function finishDeterrenceTest(
  state: DeterrenceTestState,
  sentFor: string,
  device: DeterrenceDevice,
  succeeded: boolean,
  now: number,
): DeterrenceTestState {
  if (state.subject !== sentFor) return state;
  return {
    subject: state.subject,
    sending: state.sending === device ? null : state.sending,
    sentAt: succeeded ? { ...state.sentAt, [device]: now } : state.sentAt,
  };
}

/** Drops holds that have lapsed, returning the same state when none have. */
export function releaseLapsedDeterrenceTests(state: DeterrenceTestState, now: number): DeterrenceTestState {
  const lapsed = DETERRENCE_TEST_DEVICES.filter(
    (device) => state.sentAt[device] !== undefined && !isDeterrenceTestHeld(state.sentAt[device], now),
  );
  if (lapsed.length === 0) return state;
  const sentAt: Partial<Record<DeterrenceDevice, number>> = {};
  for (const device of DETERRENCE_TEST_DEVICES) {
    const at = state.sentAt[device];
    if (at !== undefined && !lapsed.includes(device)) sentAt[device] = at;
  }
  return { ...state, sentAt };
}

export interface DeterrenceTestPrompt {
  readonly title: string;
  readonly message: string;
  readonly confirmLabel: string;
}

/**
 * The confirmation shown before a test. It warns that the test is real, and
 * that the hardware is per asset, not per camera: other cameras sharing it
 * trigger it too.
 */
const PROMPTS: Readonly<Record<DeterrenceDevice, DeterrenceTestPrompt>> = {
  siren: {
    title: "Trigger Siren",
    message:
      "This sounds the siren connected to this camera for a few seconds. Anyone nearby will hear it, and every camera sharing that siren triggers it too.",
    confirmLabel: "Trigger Siren",
  },
  strobe: {
    title: "Trigger Strobe",
    message:
      "This flashes the strobe connected to this camera for a few seconds. Anyone nearby will see it, and every camera sharing that strobe triggers it too.",
    confirmLabel: "Trigger Strobe",
  },
};

export function deterrenceTestPrompt(device: DeterrenceDevice): DeterrenceTestPrompt {
  return PROMPTS[device];
}

/**
 * Says the command was sent, not that the hardware sounded: the edge reports
 * no acknowledgement, and the asset may have no hardware wired.
 */
export const DETERRENCE_TEST_SENT_MESSAGE = "Trigger command sent";
